import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import PostMaturityInterestPanel from '../Common/PostMaturityInterestPanel';
import offlinePaymentService from '../../services/offlinePaymentService';
import indexedDB from '../../services/indexedDB';

const FieldOfficerDashboard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Search state
  const [searchType, setSearchType] = useState('nic'); // 'nic' or 'loanNumber'
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  
  // Customer/Loan data
  const [customer, setCustomer] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [payableBreakdown, setPayableBreakdown] = useState(null);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Payment history
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loanPaymentHistory, setLoanPaymentHistory] = useState([]); // All payment history for the loan (to calculate actual payments)
  const [editingPaymentHistory, setEditingPaymentHistory] = useState(null);
  const [editPaymentHistoryAmount, setEditPaymentHistoryAmount] = useState('');
  const [editPaymentHistoryNotes, setEditPaymentHistoryNotes] = useState('');
  
  
  // Tabs
  const [activeTab, setActiveTab] = useState(0);
  
  // Offline status
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Track offline payments per installment
  const [offlinePayments, setOfflinePayments] = useState({}); // { installmentId: { amount, timestamp } }
  
  // Interest change request dialog
  const [interestChangeDialogOpen, setInterestChangeDialogOpen] = useState(false);
  const [interestChangeRequestType, setInterestChangeRequestType] = useState('REMOVE'); // 'CHANGE' or 'REMOVE'
  const [interestChangeAmount, setInterestChangeAmount] = useState('');
  const [interestChangeReason, setInterestChangeReason] = useState('');
  const [submittingInterestChange, setSubmittingInterestChange] = useState(false);
  
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Helper function to perform loan search (defined early so it can be used in useEffect)
  const performLoanSearch = async (loanId) => {
    if (!loanId || !user?.userId) return;
    
    setSearching(true);
    const isOnline = navigator.onLine;

    try {
      let searchData = null;

      if (isOnline) {
        try {
          const params = { loanNumber: parseInt(loanId), collectorId: user.userId };
          const response = await api.get('/collector/search', { params });
          searchData = response.data;

          if (searchData) {
            await indexedDB.cacheSearchResult(loanId, 'loanNumber', searchData);
          }
        } catch (error) {
          console.error('Online search error:', error);
          const searchError = error.response?.data?.error || error.message || 'Search failed';
          showToast(searchError || 'Search failed. Please check your connection and try again.', 'error');
          setSearching(false);
          return;
        }
      } else {
        try {
          searchData = await indexedDB.getCachedSearchResultByLoanNumber(parseInt(loanId));
          if (searchData) {
            showToast('Showing cached data (offline mode)', 'info');
          } else {
            showToast('No cached data found. Please search while online first.', 'error');
            setSearching(false);
            return;
          }
        } catch (cacheError) {
          console.error('Error accessing cache:', cacheError);
          showToast('Cache access error. Please try again.', 'error');
          setSearching(false);
          return;
        }
      }

      // Process search results (same logic as handleSearch)
      if (searchData && searchData.customer) {
        setCustomer(searchData.customer);
        
        if (searchData.loans && searchData.loans.length > 0) {
          const activeLoans = searchData.loans.filter(loanData => {
            const status = loanData.loan?.status || loanData.status;
            return status !== 'CLOSED' && status !== 'REJECTED';
          });
          
          if (activeLoans.length === 0) {
            showToast('No active loans found for this customer', 'info');
            setSearching(false);
            return;
          }
          
          const firstLoan = activeLoans[0];
          setSelectedLoan(firstLoan.loan);
          setInstallments(firstLoan.installments || []);
          
          if (isOnline) {
            try {
              const today = new Date().toISOString().split('T')[0];
              const breakdownResponse = await api.get(`/collector/loans/${firstLoan.loan.id}/total-payable-breakdown`, {
                params: { date: today }
              });
              setPayableBreakdown(breakdownResponse.data);
              
              const historyResponse = await api.get(`/collector/loans/${firstLoan.loan.id}/payment-history`);
              setLoanPaymentHistory(historyResponse.data || []);
            } catch (error) {
              console.warn('Failed to fetch loan data:', error);
            }
          }
        } else if (searchData.loan) {
          const loanStatus = searchData.loan.status;
          if (loanStatus === 'CLOSED' || loanStatus === 'REJECTED') {
            showToast(`This loan is ${loanStatus.toLowerCase()} and cannot be accessed`, 'warning');
            setSearching(false);
            return;
          }
          
          setSelectedLoan(searchData.loan);
          setInstallments(searchData.installments || []);
          
          if (isOnline) {
            try {
              const today = new Date().toISOString().split('T')[0];
              const breakdownResponse = await api.get(`/collector/loans/${searchData.loan.id}/total-payable-breakdown`, {
                params: { date: today }
              });
              setPayableBreakdown(breakdownResponse.data);
              
              const historyResponse = await api.get(`/collector/loans/${searchData.loan.id}/payment-history`);
              setLoanPaymentHistory(historyResponse.data || []);
            } catch (error) {
              console.warn('Failed to fetch loan data:', error);
            }
          }
        } else {
          showToast('No active loans found for this customer', 'info');
        }
      } else {
        showToast('No results found', 'info');
      }
    } catch (error) {
      console.error('Search error:', error);
      showToast(error.response?.data?.error || error.message || 'Search failed', 'error');
    } finally {
      setSearching(false);
    }
  };

  // Auto-search for loan when loanId is provided in URL params
  useEffect(() => {
    const loanId = searchParams.get('loanId');
    if (loanId && user?.userId && !searching && !selectedLoan) {
      // Set search type to loanNumber and search value to loanId
      setSearchType('loanNumber');
      setSearchValue(loanId);
      // Clear the loanId from URL after reading it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('loanId');
      setSearchParams(newSearchParams, { replace: true });
      
      // Trigger search after state is updated
      const timer = setTimeout(async () => {
        await performLoanSearch(loanId);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams, user?.userId]);

  // Load offline payments for current installments
  useEffect(() => {
    const loadOfflinePayments = async () => {
      if (installments.length === 0) return;
      
      try {
        const installmentIds = installments.map(inst => inst.id);
        
        // Get full payment details for each installment
        const offlinePaymentsMap = {};
        for (const installmentId of installmentIds) {
          const payments = await indexedDB.getAllPendingPaymentsByInstallmentId(installmentId);
          if (payments && payments.length > 0) {
            // Sum all pending payments for this installment
            const totalOfflineAmount = payments
              .filter(p => !p.synced)
              .reduce((sum, p) => sum + (parseFloat(p.paymentAmount) || 0), 0);
            
            if (totalOfflineAmount > 0) {
              // Get the most recent payment timestamp
              const mostRecent = payments
                .filter(p => !p.synced)
                .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))[0];
              
              offlinePaymentsMap[installmentId] = {
                amount: totalOfflineAmount,
                timestamp: mostRecent?.timestamp || new Date().toISOString()
              };
            }
          }
        }
        
        setOfflinePayments(offlinePaymentsMap);
      } catch (error) {
        console.error('Error loading offline payments:', error);
      }
    };

    loadOfflinePayments();
    
    // Listen for offline payment updates
    const handleOfflinePaymentUpdate = () => {
      loadOfflinePayments();
    };
    
    // Listen for payment sync completion to refresh data
    const handlePaymentSync = async (event) => {
      const { syncedCount, syncedInstallmentIds } = event.detail || {};
      console.log('Payment sync completed:', { syncedCount, syncedInstallmentIds });
      
      // Refresh offline payments tracking
      await loadOfflinePayments();
      
      // If we have a selected loan and installments were synced, refresh the data
      if (selectedLoan && syncedInstallmentIds && syncedInstallmentIds.length > 0) {
        // Check if any synced installments belong to the current loan
        const shouldRefresh = installments.some(inst => 
          syncedInstallmentIds.includes(inst.id)
        );
        
        if (shouldRefresh && navigator.onLine) {
          // Wait a bit for backend to finish processing
          setTimeout(async () => {
            try {
              // Refresh installments
              const response = await api.get(`/collector/loans/${selectedLoan.id}/schedule`, {
                params: { _t: Date.now() }
              });
              const updatedInstallments = response.data.installments || [];
              setInstallments(updatedInstallments);
              
              // Refresh payable breakdown and payment history
              await fetchPayableBreakdown(selectedLoan.id);
              await fetchLoanPaymentHistory(selectedLoan.id);
              
              // If we have a selected installment, update it with fresh data
              if (selectedInstallment) {
                const updatedInstallment = updatedInstallments.find(
                  inst => inst.id === selectedInstallment.id
                );
                if (updatedInstallment) {
                  setSelectedInstallment(updatedInstallment);
                  
                  // Refresh payment history
                  const historyResponse = await api.get(
                    `/collector/installments/${updatedInstallment.id}/payment-history`
                  );
                  setPaymentHistory(historyResponse.data || []);
                }
              }
              
              showToast(`Successfully synced ${syncedCount} payment(s). Data refreshed.`, 'success');
            } catch (error) {
              console.error('Error refreshing data after sync:', error);
              showToast('Payments synced but failed to refresh data. Please refresh the page.', 'warning');
            }
          }, 1500); // Wait 1.5 seconds for backend processing
        }
      }
    };
    
    window.addEventListener('offlinePaymentUpdated', handleOfflinePaymentUpdate);
    window.addEventListener('offlinePaymentSynced', handlePaymentSync);
    
    // Also check periodically when online (to detect when payments are synced)
    let syncCheckInterval;
    if (navigator.onLine) {
      syncCheckInterval = setInterval(() => {
        loadOfflinePayments();
      }, 5000); // Check every 5 seconds
    }
    
    return () => {
      window.removeEventListener('offlinePaymentUpdated', handleOfflinePaymentUpdate);
      window.removeEventListener('offlinePaymentSynced', handlePaymentSync);
      if (syncCheckInterval) {
        clearInterval(syncCheckInterval);
      }
    };
  }, [installments, selectedLoan, selectedInstallment]);


  const handleSearch = async () => {
    if (!searchValue.trim()) {
      showToast('Please enter a search value', 'warning');
      return;
    }

    setSearching(true);
    const isOnline = navigator.onLine;
    const searchKey = searchValue.trim();

    try {
      let searchData = null;
      let searchError = null;

      if (isOnline) {
        // When online, ONLY fetch from server - do NOT use local storage
        try {
          const params = searchType === 'nic' 
            ? { nic: searchKey, collectorId: user?.userId }
            : { loanNumber: parseInt(searchKey), collectorId: user?.userId };
          
          const response = await api.get('/collector/search', { params });
          searchData = response.data;

          // Cache the search result for offline use (only if we got valid data)
          if (searchData) {
            await indexedDB.cacheSearchResult(searchKey, searchType, searchData);
            console.log('Search result cached for offline use');
          }
        } catch (error) {
          console.error('Online search error:', error);
          searchError = error.response?.data?.error || error.message || 'Search failed';
          // When online, do NOT fall back to cached data - show error instead
          showToast(searchError || 'Search failed. Please check your connection and try again.', 'error');
          setCustomer(null);
          setSelectedLoan(null);
          setInstallments([]);
          setSearching(false);
          return;
        }
      } else {
        // When offline, ONLY use local storage
        try {
          if (searchType === 'nic') {
            searchData = await indexedDB.getCachedSearchResultByNic(searchKey);
          } else {
            searchData = await indexedDB.getCachedSearchResultByLoanNumber(parseInt(searchKey));
          }

          if (searchData) {
            showToast('Showing cached data (offline mode)', 'info');
          } else {
            showToast('No cached data found. Please search while online first.', 'error');
            setCustomer(null);
            setSelectedLoan(null);
            setInstallments([]);
            setSearching(false);
            return;
          }
        } catch (cacheError) {
          console.error('Error accessing cache:', cacheError);
          showToast('Cache access error. Please try again.', 'error');
          setCustomer(null);
          setSelectedLoan(null);
          setInstallments([]);
          setSearching(false);
          return;
        }
      }

      // Process search results
      if (searchData && searchData.customer) {
        setCustomer(searchData.customer);
        
        if (searchData.loans && searchData.loans.length > 0) {
          // Check if all loans are closed or rejected
          const allLoansStatus = searchData.loans.map(loanData => {
            return loanData.loan?.status || loanData.status;
          });
          
          const hasClosedLoans = allLoansStatus.some(status => status === 'CLOSED');
          const hasRejectedLoans = allLoansStatus.some(status => status === 'REJECTED');
          
          // Filter out CLOSED and REJECTED loans (should not be shown)
          const activeLoans = searchData.loans.filter(loanData => {
            const status = loanData.loan?.status || loanData.status;
            return status !== 'CLOSED' && status !== 'REJECTED';
          });
          
          if (activeLoans.length === 0) {
            // Check if all loans are closed
            if (hasClosedLoans && !hasRejectedLoans) {
              showToast('All loans for this customer are closed and cannot be accessed', 'warning');
            } else if (hasRejectedLoans && !hasClosedLoans) {
              showToast('All loans for this customer are rejected and cannot be accessed', 'error');
            } else {
              showToast('No active loans found for this customer (all loans are closed or rejected)', 'info');
            }
            setCustomer(null);
            setSelectedLoan(null);
            setInstallments([]);
            setSearching(false);
            return;
          }
          
          // If there are closed loans but also active loans, show a message
          if (hasClosedLoans && activeLoans.length > 0) {
            showToast('Some loans for this customer are closed. Showing only active loans.', 'info');
          }
          
          // If multiple loans, select the first one
          const firstLoan = activeLoans[0];
          setSelectedLoan(firstLoan.loan);
          setInstallments(firstLoan.installments || []);
          
          // Try to fetch payable breakdown and payment history (only if online)
          if (isOnline) {
            try {
              await fetchPayableBreakdown(firstLoan.loan.id);
              await fetchLoanPaymentHistory(firstLoan.loan.id);
            } catch (error) {
              console.warn('Failed to fetch loan data:', error);
              // Continue without breakdown
            }
          }
        } else if (searchData.loan) {
          // Single loan result - check if it's CLOSED or REJECTED
          const loanStatus = searchData.loan.status;
          if (loanStatus === 'CLOSED') {
            showToast('This loan is closed and cannot be accessed', 'warning');
            setCustomer(null);
            setSelectedLoan(null);
            setInstallments([]);
            setSearching(false);
            return;
          }
          if (loanStatus === 'REJECTED') {
            showToast('This loan is rejected and cannot be accessed', 'error');
            setCustomer(null);
            setSelectedLoan(null);
            setInstallments([]);
            setSearching(false);
            return;
          }
          
          setSelectedLoan(searchData.loan);
          setInstallments(searchData.installments || []);
          
          // Try to fetch payable breakdown and payment history (only if online)
          if (isOnline) {
            try {
              await fetchPayableBreakdown(searchData.loan.id);
              await fetchLoanPaymentHistory(searchData.loan.id);
            } catch (error) {
              console.warn('Failed to fetch loan data:', error);
              // Continue without breakdown
            }
          }
        } else {
          showToast('No active loans found for this customer', 'info');
        }
      } else {
        showToast('No results found', 'info');
        setCustomer(null);
        setSelectedLoan(null);
        setInstallments([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      showToast(error.response?.data?.error || error.message || 'Search failed', 'error');
      setCustomer(null);
      setSelectedLoan(null);
      setInstallments([]);
    } finally {
      setSearching(false);
    }
  };

  const fetchPayableBreakdown = async (loanId) => {
    // Skip if offline - breakdown requires online connection
    if (!navigator.onLine) {
      console.log('Skipping payable breakdown fetch (offline)');
      return;
    }
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/collector/loans/${loanId}/total-payable-breakdown`, {
        params: { date: today }
      });
      setPayableBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching payable breakdown:', error);
    }
  };

  const fetchLoanPaymentHistory = async (loanId) => {
    // Skip if offline - payment history requires online connection
    if (!navigator.onLine) {
      console.log('Skipping loan payment history fetch (offline)');
      return;
    }
    try {
      const response = await api.get(`/collector/loans/${loanId}/payment-history`);
      const paymentHistory = response.data || [];
      console.log('Fetched payment history for loan', loanId, ':', paymentHistory.length, 'entries');
      console.log('Payment history details:', paymentHistory.map(ph => ({
        id: ph.id,
        amount: ph.amount,
        installmentNumber: ph.installment?.installmentNumber || ph.installmentNumber,
        paymentDate: ph.paymentDate
      })));
      setLoanPaymentHistory(paymentHistory);
    } catch (error) {
      console.error('Error fetching loan payment history:', error);
      setLoanPaymentHistory([]);
    }
  };

  const handlePaymentClick = async () => {
    // Automatically select the first unpaid installment based on remaining balance
    // CRITICAL: Partial installments are ALWAYS prioritized first.
    // If there is a partial installment remaining, it must be covered first before moving to the next installment.
    // Priority: PARTIAL > OVERDUE > LATE > PENDING (excluding first installment)
    let targetInstallment = null;
    
    if (installments.length > 0) {
      // Filter out first installment
      const eligibleInstallments = installments.filter(inst => inst.installmentNumber > 1);
      
      // CRITICAL: Check for PARTIAL installments FIRST - they must be prioritized
      // If there is a partial installment, it must be covered before moving to the next installment
      let partialInstallments = eligibleInstallments.filter(
        inst => inst.status === 'PARTIAL'
      );
      
      if (partialInstallments.length > 0) {
        // Sort partial installments by installment number (earliest first)
        partialInstallments.sort((a, b) => a.installmentNumber - b.installmentNumber);
        targetInstallment = partialInstallments[0];
      } else {
        // No partial installments, proceed with other statuses
        // Find overdue installments
        let unpaidInstallments = eligibleInstallments.filter(
          inst => inst.status === 'OVERDUE'
        );
        
        // If no overdue, find late
        if (unpaidInstallments.length === 0) {
          unpaidInstallments = eligibleInstallments.filter(
            inst => inst.status === 'LATE'
          );
        }
        
        // If no late, find pending
        if (unpaidInstallments.length === 0) {
          unpaidInstallments = eligibleInstallments.filter(
            inst => inst.status === 'PENDING'
          );
        }
        
        // Sort by installment number (earliest first) and select the first one
        if (unpaidInstallments.length > 0) {
          unpaidInstallments.sort((a, b) => a.installmentNumber - b.installmentNumber);
          targetInstallment = unpaidInstallments[0];
        } else {
          // If all are paid, select the first installment (excluding installment #1)
          targetInstallment = eligibleInstallments[0] || null;
        }
      }
    }
    
    if (!targetInstallment) {
      showToast('No installments available for payment', 'warning');
      return;
    }
    
    setSelectedInstallment(targetInstallment);
    setEditMode(false);
    setPaymentAmount('');
    setPaymentNotes('');
    setActiveTab(0);
    setPaymentDialogOpen(true);
    
    // Load payment history when dialog opens
    try {
      console.log('Fetching payment history for installment:', targetInstallment.id);
      const response = await api.get(`/collector/installments/${targetInstallment.id}/payment-history`);
      console.log('Payment history response:', response.data);
      const historyData = Array.isArray(response.data) ? response.data : [];
      console.log('Setting payment history:', historyData);
      setPaymentHistory(historyData);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      console.error('Error response:', error.response?.data);
      showToast('Failed to load payment history: ' + (error.response?.data?.error || error.message), 'error');
      setPaymentHistory([]);
    }
  };
  
  const isPaymentHistoryEditable = (paymentEntry, paymentHistory) => {
    // Only the latest payment (first in the sorted list) can be edited
    if (!paymentHistory || paymentHistory.length === 0) return false;
    
    // Check if this is the latest payment (first entry in the sorted list)
    const latestPayment = paymentHistory[0];
    if (paymentEntry.id !== latestPayment.id) {
      return false; // Not the latest payment, cannot edit
    }
    
    // Check 24-hour rule for this specific installment's latest payment
    if (!paymentEntry.createdAt) return true;
    try {
      // Parse the createdAt timestamp (in Sri Lanka timezone from backend)
      const paymentTime = new Date(paymentEntry.createdAt);
      if (isNaN(paymentTime.getTime())) return true; // Invalid date, allow edit
      
      const now = new Date();
      const hoursSincePayment = (now - paymentTime) / (1000 * 60 * 60);
      return hoursSincePayment <= 24;
    } catch (error) {
      console.error('Error checking editability:', error);
      return true; // On error, allow edit
    }
  };
  
  const handleEditPaymentHistory = (paymentEntry) => {
    // Check if payment is editable (must be latest payment and within 24 hours)
    if (!isPaymentHistoryEditable(paymentEntry, paymentHistory)) {
      if (paymentHistory && paymentHistory.length > 0 && paymentEntry.id !== paymentHistory[0].id) {
        showToast('Only the latest payment can be edited. Please edit the most recent payment entry.', 'warning');
      } else {
        showToast('This payment is older than 24 hours. Please use the Payment History page to request an edit.', 'warning');
      }
      return;
    }
    
    setEditingPaymentHistory(paymentEntry);
    setEditPaymentHistoryAmount(paymentEntry.amount?.toString() || '');
    setEditPaymentHistoryNotes(paymentEntry.notes || '');
  };
  
  const handleSavePaymentHistoryEdit = async () => {
    if (!editPaymentHistoryAmount || parseFloat(editPaymentHistoryAmount) <= 0) {
      showToast('Please enter a valid payment amount', 'error');
      return;
    }
    
    // Double-check editability before saving (must be latest payment and within 24 hours)
    if (!isPaymentHistoryEditable(editingPaymentHistory, paymentHistory)) {
      if (paymentHistory && paymentHistory.length > 0 && editingPaymentHistory.id !== paymentHistory[0].id) {
        showToast('Only the latest payment can be edited. Please edit the most recent payment entry.', 'error');
      } else {
        showToast('This payment is older than 24 hours and cannot be edited directly. Please use the Payment History page to request an edit.', 'error');
      }
      setEditingPaymentHistory(null);
      setEditPaymentHistoryAmount('');
      setEditPaymentHistoryNotes('');
      return;
    }
    
    setProcessingPayment(true);
    try {
      await api.put(`/collector/payment-history/${editingPaymentHistory.id}`, null, {
        params: {
          amount: parseFloat(editPaymentHistoryAmount),
          notes: editPaymentHistoryNotes || undefined,
          requireAdminApproval: false
        }
      });
      
      showToast('Payment history updated successfully', 'success');
      setEditingPaymentHistory(null);
      setEditPaymentHistoryAmount('');
      setEditPaymentHistoryNotes('');
      
      // Refresh installments first to get recalculated data
      // Add a small delay to ensure backend has finished recalculating
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (selectedLoan) {
        const response = await api.get(`/collector/loans/${selectedLoan.id}/schedule`, {
          params: { _t: Date.now() } // Cache busting
        });
        const updatedInstallments = response.data.installments || [];
        setInstallments(updatedInstallments);
        await fetchPayableBreakdown(selectedLoan.id);
        await fetchLoanPaymentHistory(selectedLoan.id); // Refresh payment history to update totalPaid calculation
        
        // Update selected installment with fresh data
        const updatedInstallment = updatedInstallments.find(
          inst => inst.id === selectedInstallment.id
        );
        if (updatedInstallment) {
          setSelectedInstallment(updatedInstallment);
          
          // Refresh payment history for the updated installment
          const historyResponse = await api.get(`/collector/installments/${updatedInstallment.id}/payment-history`);
          setPaymentHistory(historyResponse.data || []);
        }
      }
    } catch (error) {
      console.error('Error updating payment history:', error);
      showToast(error.response?.data?.error || 'Failed to update payment history', 'error');
    } finally {
      setProcessingPayment(false);
    }
  };
  
  /**
   * NOTE: This function is kept for reference but is no longer used in the UI.
   * Payment history records must always be preserved and displayed - they should never be deleted.
   * All past payments must remain visible at all times for audit and tracking purposes.
   */
  /**
   * NOTE: This function is kept for reference but is no longer used in the UI.
   * Payment history records must always be preserved and displayed - they should never be deleted.
   * All past payments must remain visible at all times for audit and tracking purposes.
   */
  const handleDeletePaymentHistory = async (paymentHistoryId) => {
    // Payment history deletion is disabled - records must always be preserved
    showToast('Payment history records cannot be deleted. All records must be preserved for audit purposes.', 'warning');
    return;
  };

  const handleSubmitInterestChangeRequest = async () => {
    if (!selectedLoan || !payableBreakdown) {
      showToast('Please select a loan first', 'error');
      return;
    }

    if (interestChangeRequestType === 'CHANGE' && (!interestChangeAmount || parseFloat(interestChangeAmount) < 0)) {
      showToast('Please enter a valid interest amount', 'error');
      return;
    }

    if (!interestChangeReason || interestChangeReason.trim() === '') {
      showToast('Please provide a reason for the interest change request', 'error');
      return;
    }

    setSubmittingInterestChange(true);
    try {
      const params = {
        loanId: selectedLoan.id,
        requestedBy: user.userId,
        requestType: interestChangeRequestType,
        reason: interestChangeReason.trim(),
      };

      if (interestChangeRequestType === 'CHANGE') {
        params.requestedInterestAmount = parseFloat(interestChangeAmount);
      }

      const response = await api.post('/collector/interest-change-requests/loan', null, { params });
      showToast('Interest change request submitted successfully. Waiting for admin approval.', 'success');
      
      // Reset form
      setInterestChangeRequestType('REMOVE');
      setInterestChangeAmount('');
      setInterestChangeReason('');
      setInterestChangeDialogOpen(false);
      
      // Optionally refresh the breakdown
      if (selectedLoan) {
        const breakdownResponse = await api.get(`/collector/loans/${selectedLoan.id}/total-payable-breakdown`, {
          params: { date: new Date().toISOString().split('T')[0] }
        });
        setPayableBreakdown(breakdownResponse.data);
      }
    } catch (error) {
      console.error('Error submitting interest change request:', error);
      showToast(error.response?.data?.error || 'Failed to submit interest change request', 'error');
    } finally {
      setSubmittingInterestChange(false);
    }
  };


  const handleProcessPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      showToast('Please enter a valid payment amount', 'error');
      return;
    }

    if (!selectedInstallment) {
      showToast('No installment selected', 'error');
      return;
    }

    // Validate payment amount doesn't exceed maximum remaining payment
    // maxAllowedPayment now represents total outstanding balance, so we validate against it directly
    const paymentValue = parseFloat(paymentAmount);
    if (paymentValue > maxAllowedPayment) {
      showToast(`Payment amount cannot exceed the maximum remaining payment of ${formatCurrency(maxAllowedPayment)}. Total outstanding balance: ${formatCurrency(maxAllowedPayment)}, Already paid: ${formatCurrency(totalPaid)}`, 'error');
      return;
    }

    setProcessingPayment(true);
    try {
      // Use offline payment service which handles both online and offline cases
      const result = await offlinePaymentService.recordPayment(selectedInstallment.id, {
        paymentAmount: parseFloat(paymentAmount),
        collectorId: user.userId,
        notes: paymentNotes || '',
      });

      if (result.synced) {
        showToast('Payment recorded successfully! SMS sent to customer.', 'success');
        // Remove from offline payments tracking
        setOfflinePayments(prev => {
          const newMap = { ...prev };
          delete newMap[selectedInstallment.id];
          return newMap;
        });
      } else {
        showToast(result.message || 'Payment saved offline. Will sync when connection is restored.', 'warning');
        // Add to offline payments tracking
        setOfflinePayments(prev => ({
          ...prev,
          [selectedInstallment.id]: {
            amount: parseFloat(paymentAmount),
            timestamp: new Date().toISOString()
          }
        }));
        // Trigger update event
        window.dispatchEvent(new CustomEvent('offlinePaymentUpdated'));
      }
      
      // Store the installment ID that was just paid
      const paidInstallmentId = selectedInstallment.id;
      
      // Only refresh data if payment was synced (online)
      // For offline payments, data will be refreshed when connection is restored
      if (result.synced) {
      // Refresh installments first to get updated data (this will show the breakdown with correct amounts)
      // Add a delay to ensure backend has finished processing and committed the transaction
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (selectedLoan) {
        const response = await api.get(`/collector/loans/${selectedLoan.id}/schedule`, {
          params: { _t: Date.now() } // Cache busting
        });
        const updatedInstallments = response.data.installments || [];
        const updatedLoan = response.data.loan || selectedLoan; // Get updated loan with status
        
        // Update loan status if it has changed (e.g., to CLOSED)
        if (updatedLoan.status !== selectedLoan.status) {
          setSelectedLoan(updatedLoan);
          if (updatedLoan.status === 'CLOSED') {
            showToast('🎉 Loan Complete! All installments have been fully paid. The loan has been marked as CLOSED.', 'success');
          }
        }
        
        setInstallments(updatedInstallments);
        await fetchPayableBreakdown(selectedLoan.id);
        
        // Fetch payment history - wait a bit longer to ensure backend has created the payment history entry
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchLoanPaymentHistory(selectedLoan.id); // Refresh payment history to update totalPaid calculation
        
        // Update the selected installment with fresh data (the one that was just paid)
        const updatedPaidInstallment = updatedInstallments.find(inst => inst.id === paidInstallmentId);
        if (updatedPaidInstallment) {
          setSelectedInstallment(updatedPaidInstallment);
        }
            
            // Check if loan is complete after payment
            // Calculate new outstanding balance from updated installments
            const newOutstandingBalance = updatedInstallments
              .filter(inst => inst.installmentNumber > 1)
              .reduce((sum, inst) => {
                const totalAmount = parseFloat(inst.totalAmount || inst.amount || 0);
                const paidAmount = parseFloat(inst.paidAmount || 0);
                const outstanding = Math.max(0, totalAmount - paidAmount);
                return sum + outstanding;
              }, 0);
            
            if (newOutstandingBalance <= 0.01 && updatedLoan.status !== 'CLOSED') {
              // If calculation shows complete but status isn't CLOSED yet, show message
              // The backend should handle this, but this is a fallback
              showToast('🎉 Loan Complete! All installments have been fully paid.', 'success');
            }
        
        // Add a small additional delay to ensure payment history is committed to database
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Refresh payment history for the installment that was just paid
        console.log('Loading payment history for paid installment:', paidInstallmentId);
        try {
          const historyResponse = await api.get(`/collector/installments/${paidInstallmentId}/payment-history`, {
            params: { _t: Date.now() } // Cache busting
          });
          console.log('Payment history response:', historyResponse.data);
          const historyData = Array.isArray(historyResponse.data) ? historyResponse.data : [];
          console.log('Payment history data length:', historyData.length);
          console.log('Setting payment history:', historyData);
          
          if (historyData.length === 0) {
            console.warn('Payment history is empty after payment. Retrying in 500ms...');
            // Retry once more after a short delay in case of transaction delay
            await new Promise(resolve => setTimeout(resolve, 500));
            const retryResponse = await api.get(`/collector/installments/${paidInstallmentId}/payment-history`, {
              params: { _t: Date.now() }
            });
            const retryData = Array.isArray(retryResponse.data) ? retryResponse.data : [];
            console.log('Retry payment history:', retryData);
            setPaymentHistory(retryData);
          } else {
            setPaymentHistory(historyData);
          }
        } catch (error) {
          console.error('Error fetching payment history after payment:', error);
          console.error('Error details:', error.response?.data);
          showToast('Payment recorded but failed to load payment history', 'warning');
          setPaymentHistory([]);
        }
      }
      
        // Switch to History tab to show the new payment (only if synced)
      setActiveTab(1);
      } else {
        // For offline payments, show a message but don't switch tabs
        // The payment will appear in history once synced
      }
      
      setPaymentAmount('');
      setPaymentNotes('');
    } catch (error) {
      console.error('Payment error:', error);
      showToast(error.response?.data?.error || 'Payment processing failed', 'error');
    } finally {
      setProcessingPayment(false);
    }
  };


  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PARTIAL': return 'warning';
      case 'OVERDUE': return 'error';
      case 'LATE': return 'error';
      default: return 'default';
    }
  };

  // Calculate summary statistics
  const summary = installments.reduce((acc, inst) => {
    if (inst.installmentNumber === 1) return acc; // Skip first installment
    
    if (inst.status === 'PAID') {
      acc.paid++;
    } else if (inst.dueDate && new Date(inst.dueDate) <= new Date()) {
      acc.arrears++;
    } else {
      acc.due++;
    }
    return acc;
  }, { paid: 0, due: 0, arrears: 0 });

  // Calculate maximum allowed payment (sum of all Total column values)
  // Calculate maximum allowed payment - this is the total outstanding balance across all installments
  // This includes: baseAmount + lateInterest + carryoverAmount - advanceAmount - paidAmount
  // For each installment: outstanding = totalAmount - paidAmount
  // Maximum Payment Allowed = Sum of all outstanding balances
  // Calculate interest and principal paid per installment from payment history
  // MUST be defined BEFORE maxAllowedPayment since maxAllowedPayment depends on it
  // IMPORTANT: Paid interest is allocated EQUALLY across all installments that have interest
  // Rule: If interest is applied to N installments, paid interest is distributed equally (totalInterestPaid / N)
  const installmentBreakdown = useMemo(() => {
    const breakdown = {};
    
    // Initialize breakdown for all installments (excluding first)
    if (installments && installments.length > 0) {
      installments
        .filter(inst => inst.installmentNumber > 1)
        .forEach(inst => {
          breakdown[inst.id] = {
            interestPaid: 0,
            principalPaid: 0
          };
        });
    }
    
    if (loanPaymentHistory && loanPaymentHistory.length > 0 && installments && installments.length > 0) {
      // Calculate total interest paid from all payment history entries
      const totalInterestPaid = loanPaymentHistory
        .filter(ph => {
          const installmentNumber = ph.installment?.installmentNumber || ph.installmentNumber;
          const isReversed = ph.reversed === true || ph.reversed === 'true';
          return installmentNumber > 1 && !isReversed;
        })
        .reduce((sum, ph) => {
          const interestAmount = parseFloat(ph.interestAmount || 0);
          return sum + interestAmount;
        }, 0);
      
      // IMPORTANT: Find ALL installments that have EVER had late interest according to the late interest rule
      // This ensures paid interest amounts continue to be displayed even after interest is fully paid
      //
      // Strategy: Only include installments that actually had late interest applied (lateInterestApplied = true)
      // or currently have late interest (lateInterest > 0). This follows the late interest rule.
      //
      // Approach:
      // 1. Get installments that currently have interest (lateInterest > 0), OR
      // 2. Get installments that have had late interest applied (lateInterestApplied = true)
      // 3. Distribute paid interest equally to this set (only installments that actually had interest)
      
      // Get installments that currently have interest OR have had late interest applied
      // This ensures we only include installments that actually had interest according to the late interest rule
      const installmentsWithInterest = installments
        .filter(inst => {
          if (inst.installmentNumber <= 1) return false; // Skip first installment
          
          // Include if currently has interest
          const hasCurrentInterest = parseFloat(inst.lateInterest || 0) > 0;
          
          // Include if late interest was applied (even if now fully paid)
          const hasLateInterestApplied = inst.lateInterestApplied === true || inst.lateInterestApplied === 'true';
          
          return hasCurrentInterest || hasLateInterestApplied;
        });
      
      // If there are installments with interest (current or historical), distribute total paid interest equally
      if (installmentsWithInterest.length > 0 && totalInterestPaid > 0) {
        const interestPerInstallment = totalInterestPaid / installmentsWithInterest.length;
        installmentsWithInterest.forEach(inst => {
          if (!breakdown[inst.id]) {
            breakdown[inst.id] = { interestPaid: 0, principalPaid: 0 };
          }
          // Round to 2 decimal places to avoid floating point issues
          breakdown[inst.id].interestPaid = Math.round(interestPerInstallment * 100) / 100;
        });
        
        // Handle rounding: distribute any remainder to the first installment with interest
        const distributedTotal = installmentsWithInterest.reduce((sum, inst) => {
          return sum + (breakdown[inst.id]?.interestPaid || 0);
        }, 0);
        const remainder = totalInterestPaid - distributedTotal;
        if (Math.abs(remainder) > 0.001 && installmentsWithInterest.length > 0) {
          const firstInstId = installmentsWithInterest[0].id;
          if (breakdown[firstInstId]) {
            breakdown[firstInstId].interestPaid = Math.round((breakdown[firstInstId].interestPaid + remainder) * 100) / 100;
          }
        }
      }
      
      // Calculate principal paid per installment from payment history
      // Principal is allocated to the installment linked in payment history
      loanPaymentHistory
        .filter(ph => {
          const installmentNumber = ph.installment?.installmentNumber || ph.installmentNumber;
          const isReversed = ph.reversed === true || ph.reversed === 'true';
          return installmentNumber > 1 && !isReversed;
        })
        .forEach(ph => {
          const installmentId = ph.installment?.id || ph.installmentId;
          if (!installmentId) return;
          
          if (!breakdown[installmentId]) {
            breakdown[installmentId] = {
              interestPaid: 0,
              principalPaid: 0
            };
          }
          
          const principalAmount = parseFloat(ph.principalAmount || 0);
          breakdown[installmentId].principalPaid += principalAmount;
        });
    }
    
    return breakdown;
  }, [loanPaymentHistory, installments]);

    const maxAllowedPayment = useMemo(() => {
    if (!installments || installments.length === 0) {
      return 0;
    }
    
    const principalOutstanding = installments
      .filter(inst => inst.installmentNumber > 1)
      .reduce((sum, inst) => {
        const baseAmount = parseFloat(inst.amount || 0);
        const paidPrincipal = parseFloat(inst.paidAmount || 0);
        const duePrincipal = Math.max(0, baseAmount - paidPrincipal);
        return sum + duePrincipal;
      }, 0);

    const postMaturityInterest = parseFloat(payableBreakdown?.postMaturityInterestBalance ?? payableBreakdown?.totalLateInterest ?? 0);
    return principalOutstanding + postMaturityInterest;
  }, [installments, payableBreakdown]);

  // Calculate total already paid - ONLY from payment history amounts
  // CRITICAL: This MUST use payment history ONLY, NOT installment paidAmount
  // Reason: installment.paidAmount includes distributed overpayments (e.g., if you pay 2000 for 1030 installment,
  //          the 970 overpayment gets distributed to other installments and added to their paidAmount)
  //          Payment history shows ONLY the actual payments made (2000 in the example above)
  //          This ensures "Maximum Payment Allowed" shows only actual payments, not distributed overpayments
  const totalPaid = useMemo(() => {
    // ONLY use payment history - never use installment paidAmount
    if (loanPaymentHistory && loanPaymentHistory.length > 0) {
      // Filter to only include payments for installments after the first one AND exclude reversed payments
      const relevantPayments = loanPaymentHistory.filter(ph => {
        const installmentNumber = ph.installment?.installmentNumber || ph.installmentNumber;
        const isReversed = ph.reversed === true || ph.reversed === 'true';
        return installmentNumber > 1 && !isReversed;
      });
      
      // Sum all payment amounts from payment history ONLY (excluding reversed payments)
      // Each payment history entry represents one actual payment made by the collector
      const actualPayments = relevantPayments.reduce((sum, ph) => {
        const amount = ph.amount || 0;
        return sum + parseFloat(amount);
      }, 0);
      
      console.log('=== Payment History Calculation (ONLY from Payment History, Excluding Reversed) ===');
      console.log('Total Payment History Entries:', loanPaymentHistory.length);
      console.log('Relevant Payments (installment > 1, not reversed):', relevantPayments.length);
      console.log('Reversed Payments:', loanPaymentHistory.filter(ph => ph.reversed === true || ph.reversed === 'true').length);
      console.log('Payment Details:', relevantPayments.map(ph => ({
        id: ph.id,
        amount: ph.amount,
        reversed: ph.reversed,
        installmentNumber: ph.installment?.installmentNumber || ph.installmentNumber
      })));
      console.log('Total Paid (ONLY from Payment History, Excluding Reversed):', actualPayments);
      console.log('===================================');
      
      return actualPayments;
    }
    
    // If payment history is not loaded yet, return 0
    // DO NOT use installment paidAmount as fallback - it includes distributed overpayments
    // This ensures we don't show incorrect values while payment history is being fetched
    console.warn('Payment history not loaded yet, using 0 for totalPaid. Will update when payment history is available.');
    return 0;
  }, [loanPaymentHistory, installments]);

  // Calculate maximum remaining payment (for validation - what can still be paid)
  // maxAllowedPayment now represents total outstanding balance (totalAmount - paidAmount for each installment)
  // So maxRemainingPaymentForValidation should just be maxAllowedPayment itself
  // But we still need to validate against totalPaid to ensure we don't exceed what's actually been paid
  const maxRemainingPaymentForValidation = maxAllowedPayment;
  
  // Maximum Payment Allowed (for display) - shows total outstanding balance
  // This is the sum of (totalAmount - paidAmount) for all installments > 1
  // It includes late interest, carryover, and accounts for already paid amounts
  const maxRemainingPayment = maxAllowedPayment; // Total outstanding balance
  
  // Check if loan is complete (all outstanding balances are zero or negative)
  const isLoanComplete = maxAllowedPayment <= 0.01; // Allow small floating point differences

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Search and Collect
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {isOffline && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Offline Mode:</strong> You are currently offline. Search will use cached data. 
                New searches require an internet connection to cache data for offline use.
              </Typography>
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Tabs
                value={searchType === 'nic' ? 0 : 1}
                onChange={(e, newValue) => setSearchType(newValue === 0 ? 'nic' : 'loanNumber')}
                sx={{ mb: 2 }}
              >
                <Tab label="NIC" />
                <Tab label="Loan Number" />
              </Tabs>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={searchType === 'nic' ? 'Customer NIC' : 'Loan Number'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={searchType === 'nic' ? 'Enter NIC number' : 'Enter Loan Number'}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={searching}
                sx={{ height: '56px' }}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Customer and Loan Information */}
      {customer && selectedLoan && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {customer.fullName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">NIC</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {customer.nic}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Address</Typography>
                  <Typography variant="body1">{customer.address}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contact</Typography>
                  <Typography variant="body1">{customer.phoneNumber}</Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Loan Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Loan Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    LOAN-{selectedLoan.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Loan Amount</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formatCurrency(selectedLoan.approvedAmount || selectedLoan.requestedAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedLoan.status} 
                    color={
                      selectedLoan.status === 'ACTIVE' ? 'success' : 
                      selectedLoan.status === 'CLOSED' ? 'default' : 
                      'default'
                    }
                    size="small"
                    sx={{
                      fontWeight: selectedLoan.status === 'CLOSED' ? 'bold' : 'normal',
                      backgroundColor: selectedLoan.status === 'CLOSED' ? '#e0e0e0' : undefined
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payable Amount Details */}
          {payableBreakdown && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Total Payable Amount (Up to Today)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
                      <Typography variant="body2">Total Payable Without Interest</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          payableBreakdown.totalPayableWithoutInterest !== undefined 
                            ? payableBreakdown.totalPayableWithoutInterest 
                            : (parseFloat(payableBreakdown.totalOverdueBase || 0) + parseFloat(payableBreakdown.currentDayBase || 0))
                        )}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: 'success.light', color: 'white' }}>
                      <Typography variant="body2">Total Payable With Interest</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          (payableBreakdown.totalPayableWithoutInterest !== undefined 
                            ? payableBreakdown.totalPayableWithoutInterest 
                            : (parseFloat(payableBreakdown.totalOverdueBase || 0) + parseFloat(payableBreakdown.currentDayBase || 0))) +
                          parseFloat(payableBreakdown.totalLateInterest || 0)
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                        Without Interest + Late Interest
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: 'warning.light', color: 'white' }}>
                      <Typography variant="body2">Late Interest Amount</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(payableBreakdown.totalLateInterest)}
                      </Typography>
                      {payableBreakdown.interestRate && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                          Rate: {parseFloat(payableBreakdown.interestRate || 0).toFixed(2)}%
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: 'info.light', color: 'white' }}>
                      <Typography variant="body2">Paid Amount</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(totalPaid)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                        From Payment History
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: totalPaid < maxRemainingPayment ? 'error.light' : 'success.light', color: 'white' }}>
                      <Typography variant="body2">Remaining Balance</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(maxRemainingPayment)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                        Maximum Payment Allowed (Outstanding Balance)
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
                
                {(payableBreakdown.proportionalOverdueCount > 0 || payableBreakdown.overdueInstallments > 0) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {payableBreakdown.proportionalOverdueCount !== undefined 
                      ? parseFloat(payableBreakdown.proportionalOverdueCount).toFixed(2)
                      : payableBreakdown.overdueInstallments} overdue installment(s) found
                  </Alert>
                )}
                
                {/* Interest Change Request Button */}
                {payableBreakdown && payableBreakdown.totalLateInterest > 0 && selectedLoan && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => setInterestChangeDialogOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      Request Interest Change/Removal
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <PostMaturityInterestPanel
            breakdown={payableBreakdown?.postMaturityInterestBreakdown}
            title="Late Payment Interest — Show Customer"
          />

          {/* Loan Schedule Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Loan Schedule Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label={`Paid: ${summary.paid}`}
                    color="success"
                    size="small"
                  />
                  <Chip 
                    icon={<ScheduleIcon />}
                    label={`Due: ${summary.due}`}
                    color="info"
                    size="small"
                  />
                  <Chip 
                    icon={<WarningIcon />}
                    label={`Arrears: ${summary.arrears}`}
                    color="error"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Installment Breakdown Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Installment-wise Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
                    This table shows how payments are applied to each installment. When a payment is made, it first clears any late interest charges, then reduces the principal amount. Each row displays the current status of one installment schedule.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {isLoanComplete && (
                    <Alert severity="success" sx={{ py: 0.5, px: 1.5, borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                        Loan Complete
                      </Typography>
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PaymentIcon />}
                    onClick={() => handlePaymentClick(null)}
                    sx={{ minWidth: 150 }}
                    disabled={isLoanComplete}
                  >
                    Make Payment
                  </Button>
                </Box>
              </Box>
              {isLoanComplete && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    🎉 All installments have been fully paid! This loan is complete.
                  </Typography>
                  {selectedLoan && selectedLoan.status === 'CLOSED' && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'success.dark' }}>
                      ✓ Loan Status: CLOSED - The loan has been automatically marked as closed.
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total Loan Amount: {formatCurrency(maxAllowedPayment)} | Total Paid: {formatCurrency(totalPaid)}
                  </Typography>
                </Alert>
              )}
              {!isLoanComplete && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Maximum Payment Allowed (Outstanding Balance):</strong> {formatCurrency(maxRemainingPayment)}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Total Outstanding: {formatCurrency(maxAllowedPayment)} | Already Paid: {formatCurrency(totalPaid)} | Remaining: {formatCurrency(Math.max(0, maxAllowedPayment - totalPaid))}
                    </Typography>
                  </Typography>
                </Alert>
              )}
              {/* Installment-wise Breakdown Table */}
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  border: '1px solid', 
                  borderColor: 'divider',
                  maxHeight: '70vh',
                  overflowX: 'auto',
                  width: '100%',
                  // Mobile responsive: ensure horizontal scrolling works smoothly
                  [theme.breakpoints.down('md')]: {
                    maxHeight: 'none',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  },
                  '&::-webkit-scrollbar': {
                    height: 8,
                    width: 8,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: 4,
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Installment #</Typography>
                            <Typography variant="caption" color="text.secondary">Schedule Number</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Due Date</Typography>
                            <Typography variant="caption" color="text.secondary">Payment Deadline</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 120 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Base Amount</Typography>
                            <Typography variant="caption" color="text.secondary">Original Loan Amount</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 130 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Late Interest</Typography>
                            <Typography variant="caption" color="text.secondary">Penalty Charges (Current)</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 130 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total Due</Typography>
                            <Typography variant="caption" color="text.secondary">Base + Interest</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 130 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Paid Interest</Typography>
                            <Typography variant="caption" color="text.secondary">Interest Already Paid</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 130 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Due Interest</Typography>
                            <Typography variant="caption" color="text.secondary">Remaining Interest to Pay</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 160 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Paid Installment Amount</Typography>
                            <Typography variant="caption" color="text.secondary">Principal Already Paid</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 160 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Due Installment Amount</Typography>
                            <Typography variant="caption" color="text.secondary">Remaining Principal to Pay</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Status</Typography>
                            <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {installments
                        .filter(inst => inst.installmentNumber > 1) // Skip first installment
                        .map((installment) => {
                        // Base amount (principal)
                        const baseAmount = installment.amount || 0;
                        // Current late interest (remaining, not yet paid)
                        const currentLateInterest = parseFloat(installment.lateInterest || 0);
                        // Total due = Base + Late Interest
                        const total = (baseAmount + currentLateInterest);
                        // Paid amount (principal only - from paidAmount field)
                        const paidPrincipal = installment.paidAmount || 0;
                        // Get breakdown from payment history (shows how much interest was paid historically)
                        const breakdown = installmentBreakdown[installment.id] || {
                          interestPaid: 0,
                          principalPaid: 0
                        };
                        
                        // Calculate remaining principal (base amount not yet paid) - Due Installment Amount
                        const remainingPrincipal = Math.max(0, baseAmount - paidPrincipal);
                        // Remaining late interest (if any) - Due Interest
                        const remainingInterest = currentLateInterest;
                        
                        // Check if there's an offline payment for this installment
                        const offlinePayment = offlinePayments[installment.id];
                        const offlineAmount = offlinePayment ? offlinePayment.amount : 0;
                        
                        // Determine status
                        let displayStatus = installment.status;
                        let statusColor = getStatusColor(installment.status);
                        if (offlinePayment) {
                          displayStatus = 'Payment Offline';
                          statusColor = 'warning';
                        }
                        
                        return (
                          <TableRow key={installment.id} hover>
                            <TableCell>{installment.installmentNumber}</TableCell>
                            <TableCell>
                              {new Date(installment.dueDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(installment.amount)}
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(installment.lateInterest || 0) > 0 ? (
                                <Box>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(installment.lateInterest)}
                                  </Typography>
                                  {selectedLoan?.loanTemplate && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Rate: {parseFloat(selectedLoan.loanTemplate.lateInterestRate || 0).toFixed(2)}%
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(0)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatCurrency(total)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                (Base: {formatCurrency(baseAmount)} + Interest: {formatCurrency(currentLateInterest)})
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {breakdown.interestPaid > 0 ? (
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(breakdown.interestPaid)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(0)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {remainingInterest > 0 ? (
                                <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(remainingInterest)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="success.main">
                                  {formatCurrency(0)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {paidPrincipal > 0 ? (
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(paidPrincipal)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(0)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {remainingPrincipal > 0 ? (
                                <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(remainingPrincipal)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="success.main">
                                  {formatCurrency(0)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={displayStatus}
                                color={statusColor}
                                size="small"
                                icon={offlinePayment ? <WarningIcon /> : undefined}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Summary Row */}
                      {installments.filter(inst => inst.installmentNumber > 1).length > 0 && (() => {
                        const filteredInstallments = installments.filter(inst => inst.installmentNumber > 1);
                        
                        // Calculate totals
                        const totalBaseAmount = filteredInstallments.reduce((sum, inst) => sum + (inst.amount || 0), 0);
                        const totalLateInterest = filteredInstallments.reduce((sum, inst) => sum + parseFloat(inst.lateInterest || 0), 0);
                        const totalDue = totalBaseAmount + totalLateInterest;
                        
                        const totalInterestPaid = filteredInstallments.reduce((sum, inst) => {
                          const breakdown = installmentBreakdown[inst.id] || { interestPaid: 0 };
                          return sum + breakdown.interestPaid;
                        }, 0);
                        
                        const totalDueInterest = filteredInstallments.reduce((sum, inst) => sum + parseFloat(inst.lateInterest || 0), 0);
                        
                        const totalPrincipalPaid = filteredInstallments.reduce((sum, inst) => sum + (inst.paidAmount || 0), 0);
                        const totalDuePrincipal = filteredInstallments.reduce((sum, inst) => {
                          const baseAmount = inst.amount || 0;
                          const paidPrincipal = inst.paidAmount || 0;
                          return sum + Math.max(0, baseAmount - paidPrincipal);
                        }, 0);
                        
                        // Determine overall status based on due amounts
                        const isComplete = totalDueInterest <= 0.01 && totalDuePrincipal <= 0.01;
                        
                        return (
                          <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider', py: 1.5 } }}>
                            <TableCell colSpan={2}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                TOTAL (All Installments)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totalBaseAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: totalLateInterest > 0 ? 'error.main' : 'text.primary' }}>
                                {formatCurrency(totalLateInterest)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totalDue)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                {formatCurrency(totalInterestPaid)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: totalDueInterest > 0 ? 'error.main' : 'success.main' }}>
                                {formatCurrency(totalDueInterest)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                {formatCurrency(totalPrincipalPaid)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: totalDuePrincipal > 0 ? 'error.main' : 'success.main' }}>
                                {formatCurrency(totalDuePrincipal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={isComplete ? 'Complete' : 'Pending'}
                                color={isComplete ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
              
              {/* Payment Application Explanation */}
              <Box sx={{ mt: 3, p: 2.5, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon sx={{ fontSize: 20 }} />
                  How Payments Are Applied
                </Typography>
                <Typography variant="body2" component="div" sx={{ pl: 4 }}>
                  <strong>Step 1:</strong> Payment first clears any <strong style={{ color: '#d32f2f' }}>Late Interest</strong> charges across all installments.
                  <br />
                  <strong>Step 2:</strong> Remaining payment amount is then applied to the <strong style={{ color: '#2e7d32' }}>Principal (Base Amount)</strong> of the selected installment.
                  <br />
                  <strong>Example:</strong> If customer pays Rs. 1,000 and has Rs. 500 in late interest, the system applies Rs. 500 to interest first, then Rs. 500 to principal.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Payment Dialog with Tabs */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedInstallment 
            ? `Installment #${selectedInstallment.installmentNumber} - Payment Management`
            : 'Loan Schedule - Payment Management'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Tabs 
              value={activeTab} 
              onChange={async (e, newValue) => {
                setActiveTab(newValue);
                // When switching to Payment History tab, reload the payment history
                if (newValue === 1 && selectedInstallment) {
                  try {
                    const response = await api.get(`/collector/installments/${selectedInstallment.id}/payment-history`);
                    setPaymentHistory(response.data || []);
                  } catch (error) {
                    console.error('Error fetching payment history:', error);
                    showToast('Failed to load payment history', 'error');
                    setPaymentHistory([]);
                  }
                }
              }} 
              sx={{ mb: 2 }}
            >
              <Tab label="Make Payment" />
              <Tab label="Payment History" />
            </Tabs>
            
            {activeTab === 0 && (
              <Box>
                {selectedInstallment && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Selected Installment: #{selectedInstallment.installmentNumber}
                    </Typography>
                    <Typography variant="body2">
                      Total Due: {formatCurrency(selectedInstallment.totalAmount || selectedInstallment.amount || 0)}
                    </Typography>
                    <Typography variant="body2">
                      Already Paid: {formatCurrency(selectedInstallment.paidAmount || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      Remaining: {formatCurrency((selectedInstallment.totalAmount || selectedInstallment.amount || 0) - (selectedInstallment.paidAmount || 0))}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Status: {selectedInstallment.status}
                    </Typography>
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPaymentAmount(value);
                    // Validate in real-time (use validation limit)
                    // Validate against total outstanding balance
                    // maxAllowedPayment already represents outstanding balance, so use it directly
                    if (value && parseFloat(value) > maxAllowedPayment) {
                      // Error will be shown in helper text
                    }
                  }}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>
                  }}
                  error={paymentAmount && parseFloat(paymentAmount) > maxAllowedPayment}
                  helperText={
                    paymentAmount && parseFloat(paymentAmount) > maxAllowedPayment
                      ? `Payment cannot exceed maximum outstanding balance of ${formatCurrency(maxAllowedPayment)}`
                      : `Enter the payment amount. Maximum allowed: ${formatCurrency(maxAllowedPayment)} (Outstanding: ${formatCurrency(maxAllowedPayment)}, Already Paid: ${formatCurrency(totalPaid)})`
                  }
                />
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                />
                {selectedInstallment && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> If the payment amount exceeds the remaining balance for this installment, 
                      the excess will automatically be applied to the next unpaid installment(s).
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
            
            {activeTab === 1 && (
              <Box>
                {!selectedInstallment ? (
                  <Alert severity="info">Please select an installment to view payment history.</Alert>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Installment #{selectedInstallment.installmentNumber}</strong> - 
                        Total Due: {formatCurrency(selectedInstallment.totalAmount || selectedInstallment.amount || 0)} | 
                        Paid: {formatCurrency(selectedInstallment.paidAmount || 0)}
                      </Typography>
                      </Box>
                      <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                        <Typography variant="body2">
                          <strong>Note:</strong> Payment history is ordered by date (latest first). 
                          Only the latest payment amount can be edited. Previous payment amounts cannot be edited.
                        </Typography>
                      </Alert>
                    </Box>
                    {(() => {
                      console.log('Payment History Tab - paymentHistory:', paymentHistory);
                      console.log('Payment History Tab - paymentHistory.length:', paymentHistory?.length);
                      console.log('Payment History Tab - selectedInstallment:', selectedInstallment);
                      
                      if (!paymentHistory || paymentHistory.length === 0) {
                        return (
                          <Alert severity="info">
                            No payment history found for this installment. Payments will appear here after they are recorded.
                          </Alert>
                        );
                      }
                      
                      return (
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell align="right"><strong>Amount</strong></TableCell>
                                <TableCell><strong>Collector</strong></TableCell>
                                <TableCell><strong>Notes</strong></TableCell>
                                <TableCell align="center"><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {/* 
                                IMPORTANT: All payment history records are always displayed.
                                Payment history records are never deleted - they are preserved for audit and tracking purposes.
                                All past payments must remain visible at all times.
                                Payment history is ordered by date (latest first).
                                Only the latest payment (first entry) can be edited.
                              */}
                              {paymentHistory.map((entry, index) => {
                                const isLatest = index === 0; // First entry is the latest (ordered by date DESC)
                                const isEditable = isPaymentHistoryEditable(entry, paymentHistory);
                                
                                return (
                                <TableRow key={entry.id} hover>
                                  <TableCell>
                                    {editingPaymentHistory?.id === entry.id ? (
                                      <TextField
                                        size="small"
                                        type="datetime-local"
                                        value={entry.createdAt 
                                          ? new Date(entry.createdAt).toISOString().slice(0, 16)
                                          : entry.paymentDate 
                                            ? new Date(entry.paymentDate + 'T00:00:00').toISOString().slice(0, 16)
                                            : ''}
                                        disabled
                                        sx={{ width: 200 }}
                                      />
                                    ) : (
                                      entry.createdAt 
                                        ? new Date(entry.createdAt).toLocaleString('en-LK', {
                                            timeZone: 'Asia/Colombo',
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: true
                                          })
                                        : entry.paymentDate
                                          ? new Date(entry.paymentDate).toLocaleDateString('en-LK', {
                                              timeZone: 'Asia/Colombo',
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric'
                                            })
                                          : '-'
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {editingPaymentHistory?.id === entry.id ? (
                                      <TextField
                                        size="small"
                                        type="number"
                                        value={editPaymentHistoryAmount}
                                        onChange={(e) => setEditPaymentHistoryAmount(e.target.value)}
                                        InputProps={{
                                          startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>
                                        }}
                                        sx={{ width: 150 }}
                                      />
                                    ) : (
                                      formatCurrency(entry.amount)
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {entry.collector?.fullName || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {editingPaymentHistory?.id === entry.id ? (
                                      <TextField
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={editPaymentHistoryNotes}
                                        onChange={(e) => setEditPaymentHistoryNotes(e.target.value)}
                                      />
                                    ) : (
                                      entry.notes || '-'
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {isLatest ? (
                                      <Chip 
                                        label={isEditable ? "Latest - Editable" : "Latest - Requires Approval"} 
                                        color={isEditable ? "success" : "warning"} 
                                        size="small" 
                                      />
                                    ) : (
                                      <Chip 
                                        label="Previous Payment" 
                                        color="default" 
                                        size="small" 
                                        variant="outlined"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {editingPaymentHistory?.id === entry.id ? (
                                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          onClick={handleSavePaymentHistoryEdit}
                                          disabled={processingPayment}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => {
                                            setEditingPaymentHistory(null);
                                            setEditPaymentHistoryAmount('');
                                            setEditPaymentHistoryNotes('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleEditPaymentHistory(entry)}
                                          title={
                                            !isEditable
                                              ? (!isLatest
                                                  ? "Only the latest payment can be edited. Previous payment amounts cannot be edited."
                                                  : "Payment older than 24 hours - use Payment History page to request edit")
                                              : "Edit Payment (latest payment, within 24 hours)"
                                          }
                                          disabled={!isEditable}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        {/* Delete button removed - Payment history records must always be preserved */}
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    })()}
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPaymentDialogOpen(false);
            setActiveTab(0);
            setPaymentAmount('');
            setPaymentNotes('');
            setEditingPaymentHistory(null);
          }}>Close</Button>
          {activeTab === 0 && (
            <Button
              variant="contained"
              onClick={handleProcessPayment}
              disabled={processingPayment || !selectedInstallment || !paymentAmount || parseFloat(paymentAmount) <= 0 || isLoanComplete || (paymentAmount && parseFloat(paymentAmount) > maxAllowedPayment)}
              startIcon={processingPayment ? <CircularProgress size={20} /> : null}
            >
              {processingPayment ? 'Processing...' : 'Record Payment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Interest Change Request Dialog */}
      <Dialog 
        open={interestChangeDialogOpen} 
        onClose={() => setInterestChangeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Interest Change/Removal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              This request will be sent to the admin for approval. You can request to change the interest amount or remove it completely.
            </Alert>
            
            <TextField
              fullWidth
              select
              label="Request Type"
              value={interestChangeRequestType}
              onChange={(e) => setInterestChangeRequestType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="REMOVE">Remove Interest (Set to 0)</MenuItem>
              <MenuItem value="CHANGE">Change Interest Amount</MenuItem>
            </TextField>

            {interestChangeRequestType === 'CHANGE' && (
              <TextField
                fullWidth
                label="New Interest Amount"
                type="number"
                value={interestChangeAmount}
                onChange={(e) => setInterestChangeAmount(e.target.value)}
                sx={{ mb: 2 }}
                helperText={`Current total interest: ${formatCurrency(payableBreakdown?.totalLateInterest || 0)}`}
                inputProps={{ min: 0, step: 0.01 }}
              />
            )}

            <TextField
              fullWidth
              label="Reason for Request"
              multiline
              rows={4}
              value={interestChangeReason}
              onChange={(e) => setInterestChangeReason(e.target.value)}
              required
              helperText="Please provide a detailed reason for this interest change request"
            />

            {payableBreakdown && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Interest Details:</strong>
                </Typography>
                <Typography variant="body2">
                  Total Late Interest: {formatCurrency(payableBreakdown.totalLateInterest || 0)}
                </Typography>
                <Typography variant="body2">
                  Interest Rate: {parseFloat(payableBreakdown.interestRate || 0).toFixed(2)}%
                </Typography>
                <Typography variant="body2">
                  Affected Installments: {payableBreakdown.overdueInstallments || 0}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterestChangeDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitInterestChangeRequest}
            variant="contained"
            color="primary"
            disabled={submittingInterestChange || !interestChangeReason.trim()}
          >
            {submittingInterestChange ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FieldOfficerDashboard;

